// Calcul du rythme de planification — FONCTION PURE (portée de la maquette). Appelée à la fois pour
// l'aperçu « Ce qui se passera » ET pour la validation, afin qu'elles ne divergent jamais (HANDOFF).

export type SchedMode = 'interval' | 'fixed' | 'cron';
export interface SchedVals { mode: SchedMode; every: number; from: string; to: string; hours: number[]; days: number[]; cron: string }

export function schedDefaults(): SchedVals {
  return { mode: 'interval', every: 2, from: '07:00', to: '20:00', hours: [7, 11, 15, 19], days: [1, 2, 3, 4, 5], cron: '' };
}

interface SchedResult { ready: boolean; problem?: string; summary?: string; detail?: string; short?: string; cronOk: boolean; cronRead?: string }

const DAY = ['', 'lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi', 'samedi', 'dimanche'];
const hOf = (t: string) => Number(String(t).slice(0, 2));
const labelOf = (days: number[]): string =>
  days.length === 7 ? 'Tous les jours'
    : String(days) === String([1, 2, 3, 4, 5]) ? 'Du lundi au vendredi'
      : String(days) === String([6, 7]) ? 'Samedi et dimanche'
        : days.length === 1 ? 'Chaque ' + DAY[days[0]!]
          : 'Les ' + days.map((d) => DAY[d]!.slice(0, 3)).join(', ');

export function schedCalc(v: SchedVals): SchedResult {
  const cronM = v.cron.trim().match(/^(\d{1,2})\s+([\d,\-*/]+)\s+\*\s+\*\s+([\d,\-*]+)$/);
  const cronHours = cronM ? (cronM[2] === '*' ? Array.from({ length: 24 }, (_, i) => i)
    : cronM[2]!.split(',').flatMap((p) => {
      const r = p.match(/^(\d{1,2})-(\d{1,2})$/);
      if (r) { const out: number[] = []; for (let i = +r[1]!; i <= +r[2]!; i++) out.push(i); return out; }
      return /^\d{1,2}$/.test(p) ? [Number(p)] : [];
    })) : [];
  const norm = (n: number | string) => (Number(n) === 0 ? 7 : Number(n));
  const cronDays = cronM ? (cronM[3] === '*' ? [1, 2, 3, 4, 5, 6, 7]
    : [...new Set(cronM[3]!.split(',').flatMap((p) => {
      const r = p.match(/^(\d)-(\d)$/);
      if (r) { const out: number[] = []; for (let i = +r[1]!; i <= +r[2]!; i++) out.push(norm(i)); return out; }
      return /^\d$/.test(p) ? [norm(p)] : [];
    }))].sort((a, b) => a - b)) : [];
  const cronMin = cronM ? String(cronM[1]).padStart(2, '0') : '00';
  const cronOk = !!cronM && cronHours.length > 0 && cronHours.every((h) => h < 24) && cronDays.length > 0 && Number(cronM![1]) < 60;

  if (v.mode !== 'cron' && !v.days.length) return { ready: false, problem: 'Choisissez au moins un jour.', cronOk };

  if (v.mode === 'interval') {
    const a = hOf(v.from), b = hOf(v.to);
    if (!(b > a)) return { ready: false, problem: 'L’heure de fin doit suivre l’heure de début.', cronOk };
    const n = Math.floor((b - a) / v.every) + 1;
    const slots = Array.from({ length: n }, (_, i) => a + i * v.every);
    return {
      ready: true, cronOk,
      summary: `${labelOf(v.days)}, toutes les ${v.every} h entre ${v.from} et ${v.to}.`,
      detail: `${n} récupérations par jour · ${slots.map((h) => String(h).padStart(2, '0') + 'h').join(' · ')} — expression générée : 0 ${slots.join(',')} * * ${v.days.join(',')}`,
      short: `${n} passages/jour`,
    };
  }
  if (v.mode === 'fixed') {
    if (!v.hours.length) return { ready: false, problem: 'Cliquez au moins une heure de déclenchement.', cronOk };
    return {
      ready: true, cronOk,
      summary: `${labelOf(v.days)}, à ${v.hours.map((h) => String(h).padStart(2, '0') + ':00').join(', ')}.`,
      detail: `${v.hours.length} récupération${v.hours.length > 1 ? 's' : ''} par jour — expression générée : 0 ${v.hours.join(',')} * * ${v.days.join(',')}`,
      short: `${v.hours.length} passage${v.hours.length > 1 ? 's' : ''}/jour`,
    };
  }
  if (!cronOk) return { ready: false, problem: 'Expression cron incomplète ou invalide.', cronOk, cronRead: '' };
  const cronTimes = cronHours.map((h) => String(h).padStart(2, '0') + 'h' + cronMin).join(', ');
  return {
    ready: true, cronOk,
    cronRead: `Se lit : ${labelOf(cronDays).toLowerCase()}, à ${cronTimes}.`,
    summary: `${labelOf(cronDays)}, à ${cronTimes}.`,
    detail: `${cronHours.length} récupération${cronHours.length > 1 ? 's' : ''} par jour · expression ${v.cron.trim()}`,
    short: `${cronHours.length} passage${cronHours.length > 1 ? 's' : ''}/jour`,
  };
}
